import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Send, Search, X, Download, File, Users, User,
  Paperclip, ArrowLeft, MoreVertical, UserPlus, UserMinus, Trash2, Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const { user, token } = useAuth();
  const [channels, setChannels] = useState([]);
  const [directChats, setDirectChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatType, setChatType] = useState('direct');
  const [messages, setMessages] = useState([]);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newDialogTab, setNewDialogTab] = useState('direct');
  const [newMessage, setNewMessage] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  // Group creation
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchGroupMember, setSearchGroupMember] = useState('');
  
  // Group management
  const [manageGroupDialogOpen, setManageGroupDialogOpen] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [searchAddMember, setSearchAddMember] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
      const interval = setInterval(() => fetchMessages(selectedChat.id), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [channelsRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/chat/channels`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/chat/users`, { headers }).then(r => r.json()).catch(() => [])
      ]);
      
      const groupChannels = channelsRes.filter(c => c.channel_type === 'group');
      const directChannels = channelsRes.filter(c => c.channel_type === 'direct');
      
      setChannels(groupChannels);
      setDirectChats(directChannels);
      setAllUsers(usersRes);
      
      // Select first available chat
      if (directChannels.length > 0) {
        setSelectedChat(directChannels[0]);
        setChatType('direct');
      } else if (groupChannels.length > 0) {
        setSelectedChat(groupChannels[0]);
        setChatType('group');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/messages/${channelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 10MB)');
        return;
      }
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setFilePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const clearFileSelection = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !selectedChat) return;
    
    try {
      let fileData = null, fileType = null, fileName = null;
      if (selectedFile) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(selectedFile);
        });
        fileType = selectedFile.type;
        fileName = selectedFile.name;
      }

      const res = await fetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          channel_id: selectedChat.id,
          content: newMessage || (fileName ? `📎 ${fileName}` : ''),
          file_data: fileData,
          file_type: fileType,
          file_name: fileName
        })
      });

      if (res.ok) {
        setNewMessage('');
        clearFileSelection();
        fetchMessages(selectedChat.id);
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    }
  };

  const handleStartDirectChat = async (otherUser) => {
    const existingChat = directChats.find(c => 
      c.members.includes(otherUser.id) && c.members.includes(user?.id)
    );

    if (existingChat) {
      setSelectedChat(existingChat);
      setChatType('direct');
      setNewDialogOpen(false);
      setShowMobileChat(true);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/chat/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${user?.first_name} & ${otherUser.first_name}`,
          channel_type: 'direct',
          members: [otherUser.id]
        })
      });

      if (res.ok) {
        const data = await res.json();
        setDirectChats([...directChats, data]);
        setSelectedChat(data);
        setChatType('direct');
        setNewDialogOpen(false);
        setShowMobileChat(true);
        toast.success(`Conversation avec ${otherUser.first_name} créée`);
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Veuillez entrer un nom de groupe');
      return;
    }
    if (selectedMembers.length === 0) {
      toast.error('Veuillez sélectionner au moins un membre');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/chat/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: groupName,
          channel_type: 'group',
          members: selectedMembers.map(m => m.id)
        })
      });

      if (res.ok) {
        const data = await res.json();
        setChannels([...channels, data]);
        setSelectedChat(data);
        setChatType('group');
        setNewDialogOpen(false);
        setGroupName('');
        setSelectedMembers([]);
        setSearchGroupMember('');
        setShowMobileChat(true);
        toast.success('Groupe créé avec succès');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du groupe');
    }
  };

  const toggleMemberSelection = (userToToggle) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === userToToggle.id);
      if (isSelected) {
        return prev.filter(m => m.id !== userToToggle.id);
      } else {
        return [...prev, userToToggle];
      }
    });
  };

  const handleAddMembersToGroup = async () => {
    if (membersToAdd.length === 0 || !selectedChat) return;

    try {
      const res = await fetch(`${API_URL}/api/chat/channels/${selectedChat.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ member_ids: membersToAdd.map(m => m.id) })
      });

      if (res.ok) {
        const updatedChannel = await res.json();
        setChannels(channels.map(c => c.id === updatedChannel.id ? updatedChannel : c));
        setSelectedChat(updatedChannel);
        setMembersToAdd([]);
        setSearchAddMember('');
        toast.success(`${membersToAdd.length} membre(s) ajouté(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout des membres');
    }
  };

  const handleRemoveMemberFromGroup = async (memberId) => {
    if (!selectedChat) return;

    try {
      const res = await fetch(`${API_URL}/api/chat/channels/${selectedChat.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const updatedChannel = await res.json();
        setChannels(channels.map(c => c.id === updatedChannel.id ? updatedChannel : c));
        setSelectedChat(updatedChannel);
        toast.success('Membre retiré du groupe');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression du membre');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedChat) return;

    try {
      const res = await fetch(`${API_URL}/api/chat/channels/${selectedChat.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const remainingChannels = channels.filter(c => c.id !== selectedChat.id);
        setChannels(remainingChannels);
        setSelectedChat(remainingChannels.length > 0 ? remainingChannels[0] : (directChats.length > 0 ? directChats[0] : null));
        setChatType(remainingChannels.length > 0 ? 'group' : 'direct');
        setDeleteGroupDialogOpen(false);
        setManageGroupDialogOpen(false);
        toast.success('Groupe supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getOtherUserInDirect = (chat) => {
    if (!chat || chat.channel_type !== 'direct') return null;
    const otherId = chat.members?.find(id => id !== user?.id);
    return allUsers.find(u => u.id === otherId);
  };

  const filteredUsers = allUsers.filter(u => 
    u.first_name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchUser.toLowerCase())
  );

  const filteredGroupUsers = allUsers.filter(u => 
    u.first_name?.toLowerCase().includes(searchGroupMember.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchGroupMember.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchGroupMember.toLowerCase())
  );

  const availableUsersForGroup = allUsers.filter(u => 
    !selectedChat?.members?.includes(u.id) &&
    (u.first_name?.toLowerCase().includes(searchAddMember.toLowerCase()) ||
     u.last_name?.toLowerCase().includes(searchAddMember.toLowerCase()))
  );

  const currentGroupMembers = selectedChat?.members?.map(memberId => 
    allUsers.find(u => u.id === memberId) || { id: memberId, first_name: 'Utilisateur', last_name: 'Inconnu' }
  ).filter(m => m.id !== user?.id) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const otherUser = chatType === 'direct' ? getOtherUserInDirect(selectedChat) : null;

  return (
    <div className="h-[calc(100vh-120px)] bg-slate-50 rounded-xl overflow-hidden border" data-testid="chat-page">
      <div className="flex h-full">
        {/* Sidebar - Liste des conversations */}
        <div className={`w-full md:w-80 bg-white border-r flex flex-col ${showMobileChat ? 'hidden md:flex' : 'flex'}`}>
          {/* Header avec bouton + */}
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Conversations</h2>
            <Button
              variant="outline"
              size="icon"
              className="rounded-lg border-2"
              onClick={() => {
                setNewDialogOpen(true);
                setNewDialogTab('direct');
                setSearchUser('');
                setGroupName('');
                setSelectedMembers([]);
                setSearchGroupMember('');
              }}
              data-testid="new-conversation-btn"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>

          {/* Liste des conversations */}
          <ScrollArea className="flex-1">
            {/* Groupes */}
            {channels.length > 0 && (
              <div className="p-2">
                <p className="text-xs font-medium text-slate-400 uppercase px-2 mb-2">Groupes</p>
                <div className="space-y-1">
                  {channels.map((channel) => {
                    const isSelected = selectedChat?.id === channel.id && chatType === 'group';
                    return (
                      <button
                        key={channel.id}
                        onClick={() => {
                          setSelectedChat(channel);
                          setChatType('group');
                          setShowMobileChat(true);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          isSelected 
                            ? 'bg-blue-50 border-2 border-blue-200' 
                            : 'hover:bg-slate-50 border-2 border-transparent'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{channel.name}</p>
                          <p className="text-sm text-slate-500">
                            {channel.members?.length || 0} membre{(channel.members?.length || 0) > 1 ? 's' : ''}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Conversations directes */}
            {directChats.length > 0 && (
              <div className="p-2">
                <p className="text-xs font-medium text-slate-400 uppercase px-2 mb-2">Messages directs</p>
                <div className="space-y-1">
                  {directChats.map((chat) => {
                    const chatUser = getOtherUserInDirect(chat);
                    const isSelected = selectedChat?.id === chat.id && chatType === 'direct';
                    
                    return (
                      <button
                        key={chat.id}
                        onClick={() => {
                          setSelectedChat(chat);
                          setChatType('direct');
                          setShowMobileChat(true);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          isSelected 
                            ? 'bg-blue-50 border-2 border-blue-200' 
                            : 'hover:bg-slate-50 border-2 border-transparent'
                        }`}
                      >
                        <Avatar className="w-12 h-12 flex-shrink-0">
                          <AvatarFallback className="bg-blue-600 text-white font-medium">
                            {chatUser ? getInitials(chatUser.first_name, chatUser.last_name) : '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">
                            {chatUser ? `${chatUser.first_name} ${chatUser.last_name}` : chat.name}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {chatUser?.position || chatUser?.department || 'Collègue'}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {channels.length === 0 && directChats.length === 0 && (
              <div className="p-4 text-center text-slate-500">
                <p className="text-sm">Aucune conversation</p>
                <p className="text-xs mt-1">Cliquez sur + pour commencer</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Zone de chat principale */}
        <div className={`flex-1 flex flex-col bg-slate-50 ${showMobileChat ? 'flex' : 'hidden md:flex'}`}>
          {selectedChat ? (
            <>
              {/* Header du chat */}
              <div className="bg-white border-b p-4 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden -ml-2"
                  onClick={() => setShowMobileChat(false)}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                
                {chatType === 'group' ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{selectedChat.name}</h3>
                      <p className="text-sm text-slate-500">
                        {selectedChat.members?.length || 0} membre{(selectedChat.members?.length || 0) > 1 ? 's' : ''}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setManageGroupDialogOpen(true)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Gérer les membres
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeleteGroupDialogOpen(true)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer le groupe
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : otherUser ? (
                  <>
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-blue-600 text-white font-medium">
                        {getInitials(otherUser.first_name, otherUser.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {otherUser.first_name} {otherUser.last_name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {otherUser.position || otherUser.department || 'Collègue'}
                      </p>
                    </div>
                  </>
                ) : null}
              </div>

              <Separator />

              {/* Zone des messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <p>Commencez la conversation...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwn = msg.user_id === user?.id;
                      const hasFile = msg.file_data && msg.file_name;
                      const isImage = msg.file_type?.startsWith('image/');
                      const msgUser = allUsers.find(u => u.id === msg.user_id);
                      
                      return (
                        <div key={msg.id} className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarFallback className="text-xs font-medium bg-blue-600 text-white">
                              {isOwn 
                                ? getInitials(user?.first_name, user?.last_name)
                                : getInitials(msgUser?.first_name || msg.user_name?.split(' ')[0], msgUser?.last_name || msg.user_name?.split(' ')[1])
                              }
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                            {chatType === 'group' && !isOwn && (
                              <p className="text-xs text-slate-500 mb-1 ml-1">{msg.user_name}</p>
                            )}
                            <div className={`rounded-2xl px-4 py-2 ${
                              isOwn 
                                ? 'bg-blue-600 text-white rounded-br-md' 
                                : 'bg-slate-200 text-slate-900 rounded-bl-md'
                            }`}>
                              {hasFile && isImage ? (
                                <div className="space-y-2">
                                  <img 
                                    src={`data:${msg.file_type};base64,${msg.file_data}`}
                                    alt={msg.file_name}
                                    className="max-w-full rounded-lg max-h-48 object-contain"
                                  />
                                  {msg.content && msg.content !== `📎 ${msg.file_name}` && (
                                    <p className="text-sm">{msg.content}</p>
                                  )}
                                </div>
                              ) : hasFile ? (
                                <div className="space-y-2">
                                  <a 
                                    href={`data:${msg.file_type};base64,${msg.file_data}`}
                                    download={msg.file_name}
                                    className={`flex items-center gap-2 p-2 rounded ${isOwn ? 'bg-blue-500' : 'bg-white'}`}
                                  >
                                    <File className="w-4 h-4" />
                                    <span className="text-sm truncate">{msg.file_name}</span>
                                    <Download className="w-4 h-4 ml-auto" />
                                  </a>
                                  {msg.content && msg.content !== `📎 ${msg.file_name}` && (
                                    <p className="text-sm">{msg.content}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              )}
                            </div>
                            <p className={`text-xs text-slate-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                              {format(new Date(msg.created_at), 'HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Prévisualisation du fichier */}
              {selectedFile && (
                <div className="px-4 py-2 border-t bg-white">
                  <div className="flex items-center gap-3">
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                        <File className="w-5 h-5 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={clearFileSelection}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Zone de saisie */}
              <div className="p-4 bg-white border-t">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  <div className="flex-1 relative">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrivez votre message..."
                      className="pr-10 rounded-full border-slate-300 focus:border-blue-500"
                      data-testid="message-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                  <Button
                    type="submit"
                    size="icon"
                    className="rounded-full w-10 h-10 bg-blue-600 hover:bg-blue-700 flex-shrink-0"
                    disabled={!newMessage.trim() && !selectedFile}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-slate-400" />
                </div>
                <p className="font-medium">Sélectionnez une conversation</p>
                <p className="text-sm mt-1">ou créez-en une nouvelle</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog nouvelle conversation / groupe */}
      <Dialog open={newDialogOpen} onOpenChange={(open) => {
        setNewDialogOpen(open);
        if (!open) {
          setSearchUser('');
          setGroupName('');
          setSelectedMembers([]);
          setSearchGroupMember('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
            <DialogDescription>
              Créez une conversation directe ou un groupe
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={newDialogTab} onValueChange={setNewDialogTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="direct" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Direct
              </TabsTrigger>
              <TabsTrigger value="group" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Groupe
              </TabsTrigger>
            </TabsList>

            {/* Conversation directe */}
            <TabsContent value="direct" className="mt-4 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  placeholder="Rechercher un collègue..."
                  className="pl-10"
                />
              </div>
              <ScrollArea className="h-64">
                {filteredUsers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucun utilisateur trouvé</p>
                ) : (
                  <div className="space-y-1">
                    {filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => handleStartDirectChat(u)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 text-left transition-colors"
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-blue-600 text-white">
                            {getInitials(u.first_name, u.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-slate-500">{u.position || u.department || u.email}</p>
                        </div>
                        {u.role === 'admin' && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">Admin</Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Création de groupe */}
            <TabsContent value="group" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Nom du groupe *</Label>
                <Input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Ex: Équipe Marketing, Projet Alpha..."
                />
              </div>

              {/* Membres sélectionnés */}
              {selectedMembers.length > 0 && (
                <div className="space-y-2">
                  <Label>Membres sélectionnés ({selectedMembers.length})</Label>
                  <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                    {selectedMembers.map((member) => (
                      <Badge key={member.id} variant="secondary" className="flex items-center gap-1 pr-1">
                        <span>{member.first_name} {member.last_name}</span>
                        <button
                          type="button"
                          onClick={() => toggleMemberSelection(member)}
                          className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Ajouter des membres *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    value={searchGroupMember}
                    onChange={(e) => setSearchGroupMember(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-10"
                  />
                </div>
                <ScrollArea className="h-40 border rounded-lg">
                  <div className="p-2 space-y-1">
                    {filteredGroupUsers.map((u) => {
                      const isSelected = selectedMembers.some(m => m.id === u.id);
                      return (
                        <div
                          key={u.id}
                          onClick={() => toggleMemberSelection(u)}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-blue-100 border border-blue-300' : 'hover:bg-slate-100'
                          }`}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className={`text-xs ${isSelected ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                              {getInitials(u.first_name, u.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-slate-500">{u.department || u.email}</p>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setNewDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleCreateGroup}
                  disabled={!groupName.trim() || selectedMembers.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Créer ({selectedMembers.length})
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Dialog gestion des membres du groupe */}
      <Dialog open={manageGroupDialogOpen} onOpenChange={(open) => {
        setManageGroupDialogOpen(open);
        if (!open) {
          setMembersToAdd([]);
          setSearchAddMember('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gérer les membres</DialogTitle>
            <DialogDescription>
              {selectedChat?.name} - {selectedChat?.members?.length || 0} membre(s)
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="current">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="current">Membres actuels</TabsTrigger>
              <TabsTrigger value="add">Ajouter</TabsTrigger>
            </TabsList>

            <TabsContent value="current" className="mt-4">
              <ScrollArea className="h-64 border rounded-lg">
                {currentGroupMembers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucun autre membre</p>
                ) : (
                  <div className="p-2 space-y-1">
                    {currentGroupMembers.map((member) => (
                      <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="text-xs bg-slate-200">
                            {getInitials(member.first_name, member.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{member.first_name} {member.last_name}</p>
                          <p className="text-xs text-slate-500">{member.email || member.department}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveMemberFromGroup(member.id)}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="add" className="mt-4 space-y-4">
              {membersToAdd.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                  {membersToAdd.map((member) => (
                    <Badge key={member.id} variant="secondary" className="flex items-center gap-1 pr-1">
                      <span>{member.first_name} {member.last_name}</span>
                      <button
                        onClick={() => setMembersToAdd(prev => prev.filter(m => m.id !== member.id))}
                        className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={searchAddMember}
                  onChange={(e) => setSearchAddMember(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-10"
                />
              </div>
              
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {availableUsersForGroup.map((u) => {
                    const isSelected = membersToAdd.some(m => m.id === u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => {
                          if (isSelected) {
                            setMembersToAdd(prev => prev.filter(m => m.id !== u.id));
                          } else {
                            setMembersToAdd(prev => [...prev, u]);
                          }
                        }}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-100 border border-blue-300' : 'hover:bg-slate-100'
                        }`}
                      >
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className={`text-xs ${isSelected ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                            {getInitials(u.first_name, u.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-slate-500">{u.department || u.email}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {membersToAdd.length > 0 && (
                <Button onClick={handleAddMembersToGroup} className="w-full bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter {membersToAdd.length} membre{membersToAdd.length > 1 ? 's' : ''}
                </Button>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManageGroupDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation suppression groupe */}
      <AlertDialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le groupe ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le groupe <strong>"{selectedChat?.name}"</strong> ?
              Cette action supprimera également tous les messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGroup} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
